import PropTypes from 'prop-types';
import { Archive, Calendar, Edit, Eye, MoreVertical, Trash2, Users } from 'lucide-react';
import { Badge, Button } from '../ui';
import { EmptyState } from '../common';

/**
 * Composant de présentation pur pour afficher une liste de sondages
 * @component
 */
function SurveyList({
  surveys,
  onView,
  onEdit,
  onArchive,
  onDelete,
  isLoading = false,
  computeProgress,
  getStatusConfig,
}) {
  if (surveys.length === 0) {
    return (
      <EmptyState
        icon="surveys"
        title="Aucun sondage trouvé"
        description="Aucun sondage n'a été créé pour le moment."
      />
    );
  }

  return (
    <div className="space-y-3">
      {surveys.map((survey) => {
        const status = getStatusConfig(survey.computed_status);
        const responses = survey.responses_count ?? survey.reponses ?? 0;
        const target = survey.target_responses ?? survey.expected_responses ?? survey.limit_responses ?? survey.max_responses ?? 200;
        const progress = computeProgress(responses, target);

        return (
          <div 
            key={survey.id} 
            className="rounded-lg border bg-card text-card-foreground shadow-sm shadow-card border-0 hover:shadow-elevated transition-all duration-300 cursor-pointer"
            onClick={() => onView && onView(survey)}
          >
            <div className="p-5">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                {/* Contenu principal */}
                <div className="space-y-2 min-w-0 flex-1">
                  {/* Titre et badges */}
                  <div className="flex items-center gap-3 flex-wrap">
                    <h3 className="font-display font-semibold text-foreground">{survey.title}</h3>
                    <Badge variant="outline" className={status.className}>{status.label}</Badge>
                    <Badge variant="outline" className="text-xs">
                      {survey.category === "EVENT" ? "événementiel" : survey.category === "INTERNAL" ? "interne" : survey.category === "EXTERNAL" ? "externe" : survey.category}
                    </Badge>
                    {survey.archived && <Badge variant="outline" className="text-xs bg-muted">Archivé</Badge>}
                  </div>
                  <p className="text-sm text-muted-foreground">{survey.description}</p>
                  {/* Infos secondaires */}
                  <div className="flex items-center gap-4 text-xs text-muted-foreground">
                    <span>Créé par : {survey.creator_username}</span>
                    <span className="flex items-center gap-1"><Users className="h-3 w-3" />{responses} réponses</span>
                    {survey.start_date && <span className="flex items-center gap-1"><Calendar className="h-3 w-3" />{survey.start_date} → {survey.end_date}</span>}
                  </div>
                </div>

                {/* Taux de complétion et actions */}
                <div className="flex items-center gap-4 ml-4">
                  {/* Barre de progression */}
                  {target > 0 && (
                    <div className="text-right">
                      <p className="text-sm font-semibold text-foreground">{progress}%</p>
                      <div className="w-20 h-1.5 bg-muted rounded-full overflow-hidden mt-1">
                        <div className="h-full rounded-full bg-primary transition-all duration-500" style={{ width: `${progress}%` }} />
                      </div>
                    </div>
                  )} 

                  {/* Menu actions */}
                  <div className="relative group" onClick={(e) => e.stopPropagation()}>
                    <div className="flex cursor-default select-none items-center rounded-sm px-2 py-1.5 text-sm outline-none data-[state=open]:bg-accent focus:bg-accent">
                      <Button disabled={isLoading} variant="ghost" size="icon"><MoreVertical className="h-4 w-4" /></Button>
                    </div>

                    {/* Dropdown menu */}
                    <div className="absolute right-0 z-50 min-w-[9rem] rounded-md border bg-popover p-1 text-popover-foreground shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2">
                      <div className='flex items-center flex-col p-1 gap-2'>
                        {onView && (
                          <button
                            onClick={(e) => { e.stopPropagation(); onView(survey); }}
                            disabled={isLoading}
                            className="w-full flex items-center select-none items-center rounded-sm px-2 py-1.5 text-sm outline-none transition-colors focus:bg-accent focus:text-accent-foreground hover:text-white hover:bg-accent"
                          >
                            <Eye className="h-4 w-4 mr-2" />Voir détails
                          </button>
                        )}
                        {onEdit && (
                          <button
                            onClick={(e) => { e.stopPropagation(); onEdit(survey); }}
                            disabled={isLoading}
                            className="w-full flex items-center select-none items-center rounded-sm px-2 py-1.5 text-sm outline-none transition-colors focus:bg-accent focus:text-accent-foreground hover:text-white hover:bg-accent"
                          >
                            <Edit className="h-4 w-4 mr-2" />Modifier
                          </button>
                        )}
                        {onArchive && survey.status !== 'archived' && (
                          <button
                            onClick={(e) => { e.stopPropagation(); onArchive(survey); }}
                            disabled={isLoading}
                            className="w-full flex items-center select-none items-center rounded-sm px-2 py-1.5 text-sm outline-none transition-colors focus:bg-accent focus:text-accent-foreground hover:text-white hover:bg-accent"
                          >
                            <Archive className="h-4 w-4 mr-2" />Archiver
                          </button>
                        )}
                        {onDelete && (
                          <button
                            onClick={(e) => { e.stopPropagation(); onDelete(survey); }}
                            disabled={isLoading}
                            className="w-full flex items-center select-none items-center rounded-sm px-2 py-1.5 text-sm text-destructive outline-none transition-colors focus:bg-accent focus:text-accent-foreground hover:text-white hover:bg-accent"
                          >
                            <Trash2 className="h-4 w-4 mr-2" />Supprimer
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

SurveyList.propTypes = {
  surveys: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.number.isRequired,
    title: PropTypes.string.isRequired,
    description: PropTypes.string,
    computed_status: PropTypes.string,
    responses_count: PropTypes.number,
    reponses: PropTypes.number,
    target_responses: PropTypes.number,
    expected_responses: PropTypes.number,
    limit_responses: PropTypes.number,
    max_responses: PropTypes.number,
    category: PropTypes.string,
    archived: PropTypes.bool,
    creator_username: PropTypes.string,
    start_date: PropTypes.string,
    end_date: PropTypes.string,
    status: PropTypes.string,
  })).isRequired,
  onView: PropTypes.func,
  onEdit: PropTypes.func,
  onArchive: PropTypes.func,
  onDelete: PropTypes.func,
  isLoading: PropTypes.bool,
  computeProgress: PropTypes.func.isRequired,
  getStatusConfig: PropTypes.func.isRequired,
};

SurveyList.defaultProps = {
  isLoading: false,
  onView: null,
  onEdit: null,
  onArchive: null,
  onDelete: null,
};

export default SurveyList;
